import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, output } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import {
    SelectedNode,
    SynopticFilterOption,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { BayDrawerNavigationComponent } from '@connect-services/client/components/synoptic/synoptic-plan/synoptic-plan-header/bay-drawer-navigation/bay-drawer-navigation.component';
import { SynopticWindowButtonComponent } from '@connect-services/client/components/synoptic/synoptic-window-button/synoptic-window-button.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { PlanTypeEnum } from '@connect-services/client/sdk/api';
import { HomeService } from '@connect-services/client/services/home.service';
import { ProjectEditModeService } from '@connect-services/client/services/project-edit-mode.service';
import { ExportPlanService } from '@connect-services/client/services/synoptic/export-plan.service';
import { SynopticGraphService } from '@connect-services/client/services/synoptic/synoptic-graph.service';
import { SynopticPlanInteractionService } from '@connect-services/client/services/synoptic/synoptic-plan-interaction.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { SelectComponent } from '@connect-services/src/lib/components/select/select.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { ToggleComponent } from '@shared/client/components/toggle/toggle.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        BayDrawerNavigationComponent,
        ButtonComponent,
        RessourcesModule,
        SelectComponent,
        SynopticWindowButtonComponent,
        ToggleComponent,
    ],
    selector: 'app-synoptic-plan-header',
    styleUrl: './synoptic-plan-header.component.scss',
    templateUrl: './synoptic-plan-header.component.html',
})
export class SynopticPlanHeaderComponent implements OnInit {
    public readonly close = output<void>();
    public readonly drawerPosition = input.required<string>();
    public readonly drawerSize = input.required<string>();
    public readonly enterEdition = output<void>();
    public readonly filterOptions = input.required<SynopticFilterOption[]>();
    public readonly planItems = input.required<SynopticPlanItem[]>();
    public readonly selectedNode = input.required<SelectedNode>();

    protected readonly cassetteOptions = computed<{ label: string; value: string }[]>(() =>
        this.filterOptions().map((option: SynopticFilterOption) => ({
            label: option.text,
            value: option.key,
        })),
    );
    protected readonly headerValue = computed<string>(() => {
        if (this.planItems().length) {
            return this.selectedNode()?.dataNode.label ?? '';
        }

        return this.translocoService.translate('synopticPlan.noAvailableData');
    });
    protected readonly isFiberingEditionAvailable = computed<boolean>(() => {
        return this.homeService.isExternalBoxPlanPage() && this.projectEditModeService.isEditModeActive();
    });
    protected readonly selectedCassetteOption = computed<{ label: string; value: string }[]>(() => {
        const key = this.synopticPlanInteractionService.selectedCassetteKey();
        if (!key) {
            return [];
        }
        const option = this.filterOptions().find((line: SynopticFilterOption) => line.key === key);

        return option ? [{ label: option.text, value: option.key }] : [];
    });
    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;
    protected readonly homeService = inject(HomeService);
    protected readonly PlanTypeEnum = PlanTypeEnum;
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly synopticPlanInteractionService = inject(SynopticPlanInteractionService);

    private readonly broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.EDIT_MODE);

    private readonly exportPlanService = inject(ExportPlanService);
    private readonly projectEditModeService = inject(ProjectEditModeService);
    private readonly synopticGraphService = inject(SynopticGraphService);
    private readonly translocoService = inject(TranslocoService);

    public ngOnInit(): void {
        this.broadcastChannel.onmessage = (
            event: MessageEvent<BroadcastChannelEvent<{ isEditModeActive: boolean }>>,
        ): void => {
            if (event.data.source !== BroadcastChannelSourceEnum.HOMEPAGE || !event.data.additionalData) {
                return;
            }
            this.projectEditModeService.isEditModeActive.set(event.data.additionalData.isEditModeActive);
        };
    }

    protected exportPlan(plan: PlanTypeEnum): void {
        void this.exportPlanService.planToExcel(
            plan,
            this.synopticGraphService.focusedNode()!.dataNode,
            this.planItems(),
        );
    }

    protected selectCassette(key: string | undefined): void {
        if (!key) {
            this.synopticPlanInteractionService.selectedCassetteKey.set(null);

            return;
        }

        this.synopticPlanInteractionService.selectCassetteByKey(key);
    }
}
